import { dragLayer } from "../../pixi-components/mainContainer/mainContainer";
import { checkAABB } from "../../utils/geometry";
import type { Box } from "../coreComponents/Box";
import {
  getInstances,
  getNodeForKey,
  getPixiParentInstance
} from "../renderingEngine/renderer";
import { spatialRegistry } from "./spatialRegistry";

export const getNodesAtPoint = ({ x, y }: { x: number; y: number }) => {
  const ids = spatialRegistry.retrieve({ x, y });
  // Test AABB before returning, such that given point must be within the
  // dimensions of the component.
  const instances = getInstances();
  const components = ids.map(id => instances[id]);
  const filteredComponents = components.filter(component => {
    if (!component) {
      // Reachable only when `instances[id]` is undefined
      return false;
    }
    const layoutValues = component.getLayoutValues();
    return checkAABB(layoutValues, { x, y });
  });

  // higher Z-Index comes first
  filteredComponents.sort((a, b) => b.getZIndex() - a.getZIndex());
  return filteredComponents;
};

// get interactive nodes at a given point i'e nodes that have onClick, onDrag or onDragStart.
export const getInteractiveNodesAtPoint = ({
  x,
  y
}: {
  x: number;
  y: number;
}) => {
  const ids = spatialRegistry.retrieve({ x, y });
  // Test AABB before returning, such that given point must be within the
  // dimensions of the component.
  const instances = getInstances();
  const components = ids.map(id => instances[id]);
  const filteredComponents = components.filter(component => {
    if (!component) {
      // Reachable only when `instances[id]` is undefined
      return false;
    }
    const layoutValues = component.getLayoutValues();
    const isWithinBounds = checkAABB(layoutValues, { x, y });

    // check if the component has onClick or onDragStart
    const { eventHandlers } = (component as Box).props;

    if (
      isWithinBounds &&
      (eventHandlers?.onClick ||
        eventHandlers?.onDrag ||
        eventHandlers?.onDragStart)
    ) {
      return true;
    }
    return false;
  });
  return filteredComponents.sort((a, b) => b.getZIndex() - a.getZIndex());
};

export const moveNodeToDragLayer = ({
  node,
  x,
  y
}: {
  node: Box;
  x: number;
  y: number;
}) => {
  const parentInstance = getPixiParentInstance(node.props.key);
  if (parentInstance) {
    parentInstance.pixiComponent.removeChild(node.pixiComponent);
    dragLayer.position.set(x, y);
    dragLayer.addChild(node.pixiComponent);
  }
};

export const moveNodeToMainLayer = (node: Box) => {
  const metaNode = getNodeForKey(node.props.key);
  if (!metaNode) {
    throw Error(`Node with key: ${node.props.key} not found`);
  }

  const index = metaNode.index;

  const parentInstance = getPixiParentInstance(node.props.key);
  if (parentInstance) {
    dragLayer.removeChild(node.pixiComponent);
    parentInstance.pixiComponent.addChildAt(node.pixiComponent, index);
  }
};
